import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet as WalletIcon, ArrowDownToLine, TrendingUp, Clock, CheckCircle2 } from "lucide-react";
import { Spinner, StatusBadge } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import api, { apiError } from "../lib/api";
import { rupiah, fmtDate } from "../lib/format";
import { useI18n } from "../lib/i18n";

export default function WalletPage() {
  const { t } = useI18n();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Bank Transfer");
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); api.get("/wallet").then((r) => setW(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const withdraw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { amount: Number(amount), method });
      toast.success("Penarikan diminta");
      setOpen(false); setAmount(""); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="max-w-2xl space-y-5">
      <h1 className="font-heading font-extrabold text-2xl text-navy flex items-center gap-2"><WalletIcon className="text-sky" /> {t("nav_wallet")}</h1>

      <div className="bg-navy text-white rounded-3xl p-6">
        <div className="text-white/60 text-sm">{t("available")}</div>
        <div className="font-heading font-black text-3xl mt-1">{rupiah(w.available)}</div>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="bg-white/10 rounded-2xl p-3"><div className="text-white/60 text-xs flex items-center gap-1"><Clock size={12} /> {t("pending")}</div><div className="font-bold mt-1">{rupiah(w.pending)}</div></div>
          <div className="bg-white/10 rounded-2xl p-3"><div className="text-white/60 text-xs flex items-center gap-1"><TrendingUp size={12} /> {t("total_earned")}</div><div className="font-bold mt-1">{rupiah(w.total_earned)}</div></div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="withdraw-btn"
          className="mt-5 w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 flex items-center justify-center gap-2 transition-colors">
          <ArrowDownToLine size={17} /> {t("withdraw")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl p-4 shadow-soft border border-black/5"><div className="text-xs text-muted-foreground">Completed orders</div><div className="font-heading font-bold text-navy text-xl">{w.completed_orders}</div></div>
        <div className="bg-white rounded-2xl p-4 shadow-soft border border-black/5"><div className="text-xs text-muted-foreground">Platform fees</div><div className="font-heading font-bold text-navy text-xl">{rupiah(w.platform_fees)}</div></div>
      </div>

      {w.withdrawals.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-navy mb-2">Withdrawals</h2>
          <div className="space-y-2">
            {w.withdrawals.map((wd) => (
              <div key={wd.id} className="flex items-center justify-between bg-white rounded-2xl p-3 shadow-soft border border-black/5">
                <div><div className="font-semibold text-navy text-sm">{rupiah(wd.amount)}</div><div className="text-xs text-muted-foreground">{wd.method} · {fmtDate(wd.created_at)}</div></div>
                <StatusBadge status={wd.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-heading font-bold text-navy mb-2">Transaction history</h2>
        <div className="space-y-2">
          {w.transactions.length === 0 && <p className="text-sm text-muted-foreground">Belum ada transaksi.</p>}
          {w.transactions.map((tx) => (
            <div key={tx.id} className="flex items-center justify-between bg-white rounded-2xl p-3 shadow-soft border border-black/5">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.amount >= 0 ? "bg-lime-light" : "bg-orange-100"}`}><CheckCircle2 size={15} className="text-navy" /></div>
                <div><div className="text-sm font-medium text-ink">{tx.description}</div><div className="text-xs text-muted-foreground">{fmtDate(tx.created_at)}</div></div>
              </div>
              <div className={`font-bold text-sm ${tx.amount >= 0 ? "text-lime-dark" : "text-orange-600"}`}>{tx.amount >= 0 ? "+" : ""}{rupiah(tx.amount)}</div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="withdraw-dialog">
          <DialogHeader><DialogTitle className="font-heading text-navy">{t("withdraw")}</DialogTitle></DialogHeader>
          <form onSubmit={withdraw} className="space-y-3">
            <div><label className="text-sm font-semibold text-ink">Amount (Rp)</label>
              <Input data-testid="withdraw-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl mt-1" />
              <p className="text-xs text-muted-foreground mt-1">Tersedia: {rupiah(w.available)}</p></div>
            <div><label className="text-sm font-semibold text-ink">Method</label>
              <select data-testid="withdraw-method" value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-9 rounded-xl border border-input px-3 text-sm mt-1 bg-transparent">
                {["Bank Transfer", "GoPay", "OVO", "DANA"].map((m) => <option key={m}>{m}</option>)}
              </select></div>
            <button type="submit" disabled={busy} data-testid="submit-withdraw" className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 transition-colors">Withdraw</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
